import { ContentContainer, Spinner } from '@ahc-digital/component-library';
import {
  constants,
  ComponentRendering,
  GetServerSideComponentProps,
  LayoutServiceData,
  useSitecoreContext,
} from '@sitecore-jss/sitecore-jss-nextjs';
import Script from 'next/script';
import { useState } from 'react';
import { sparkleUtils } from 'src/shared/sparkle-utils';
import { useSparkleApi } from 'src/shared/SparkleApi.service';
import styles from './E01EventDetail.module.scss';
import { E01EventDetailProps } from './E01EventDetail.types';
import { GetServerSidePropsContext } from 'next';
import { ImportedData, ImportedDataType } from 'lib/imported-data/ImportedData.types';
import { ImportedDataProviderFactory } from 'lib/imported-data';
import { ImportedMetaData } from 'src/shared/ImportedMetaData';
import { useCreateMarketingRenderingHame } from 'src/shared/useCreateMarketingRenderingName';
import { translationClasses } from 'src/shared/TranslationClasses';

export const Default = (props?: E01EventDetailProps): JSX.Element => {
  const sparkleApi = useSparkleApi();
  const { sitecoreContext } = useSitecoreContext();
  const [isError, setIsError] = useState(false);
  const marketingRenderingName = useCreateMarketingRenderingHame(
    props?.rendering?.componentName as string
  );

  const isWildcardPage = sitecoreContext.route?.name === '*';

  const applyMetadata = async (eventSlug: string) => {
    const { isSuccess, textContent: metaDataHtml } = await sparkleApi.fetchEventMetadata(eventSlug);

    if (!isSuccess) {
      setIsError(true);
    }

    const doc = new DOMParser().parseFromString(metaDataHtml, 'text/html');

    // Replace metadata tags
    [
      'title',
      'meta[name="description"]',
      'meta[name="keywords"]',
      'meta[property="og:title"]',
      'meta[property="og:description"]',
      'meta[property="og:locale"]',
      'meta[property="og:site_name"]',
      'meta[property="og:type"]',
      'meta[property="og:image"]',
      'meta[property="og:url"]',
      'link[rel="canonical"]',
      'meta[property="article:published_time"]',
      'meta[property="article:modified_time"]',
      'meta[property="article:section"]',
      'meta[name="breadcrumb"]',
      'script[type="application/ld+json"]',
    ].forEach((selector: string) => {
      const pageNode = document.querySelector(selector);
      const snippetNode = doc.querySelector(selector);
      if (pageNode && snippetNode) {
        pageNode.replaceWith(snippetNode.cloneNode(true));
        snippetNode.remove();
      }
    });

    // Replace breadcrumb page title
    const breadcrumbPage = document.querySelector('[data-testid="breadcrumb-page"]');
    const newTitle = document.querySelector('meta[name="breadcrumb"]');
    if (newTitle && breadcrumbPage) {
      breadcrumbPage.textContent = newTitle.getAttribute('content');
    }

    const head = document.head || document.getElementsByTagName('head')[0];
    doc.head.childNodes.forEach((node) => {
      head.appendChild(node.cloneNode(true));
    });
  };

  const applyContent = async (eventSlug: string) => {
    const eventContentDiv = document.getElementById('event-content');
    if (!eventContentDiv) return;

    const { isSuccess, textContent: htmlContent } = await sparkleApi.fetchEventContent(eventSlug);

    if (!isSuccess) {
      setIsError(true);
      return;
    }

    sparkleUtils.applyHtmlSnippet(htmlContent, eventContentDiv);
  };

  return (
    <>
      <ImportedMetaData data={props?.importedData}></ImportedMetaData>
      <Script
        src="https://code.jquery.com/jquery-3.7.1.min.js"
        integrity="sha256-/JqT3SQfawRcv/BIHPThkBvs0OEvtFFmqPF/lYI/Cxo="
        crossOrigin="anonymous"
        onError={() => setIsError(true)}
        onReady={() => {
          const eventSlug = window.location.pathname.split('/').pop();
          if (!eventSlug) return;

          if (isWildcardPage) {
            applyMetadata(eventSlug);
          }
          applyContent(eventSlug);
        }}
      />
      <div
        id="event-content"
        className={(styles['event-content'], translationClasses(props?.params))}
        data-component={marketingRenderingName}
      >
        {isError ? (
          <ContentContainer
            headline="Something went wrong"
            headlineTag="h3"
            spacing="40-24"
            supportCopy={<p>There was an error loading this event. Please try again later.</p>}
          />
        ) : (
          <ContentContainer spacing="40-24">
            <Spinner />
          </ContentContainer>
        )}
      </div>
    </>
  );
};

export const getServerSideProps: GetServerSideComponentProps = async (
  _rendering: ComponentRendering,
  _layoutData: LayoutServiceData,
  context: GetServerSidePropsContext
) => {
  if (process.env.JSS_MODE === constants.JSS_MODE.DISCONNECTED) {
    return { importedData: {} as ImportedData };
  }
  const slug = sparkleUtils.getSlug(context.req.url ?? '');
  if (sparkleUtils.isEmptyOrWhiteSpace(slug)) return;

  const provider = ImportedDataProviderFactory.create();
  const result = await provider.getDataFor(slug, ImportedDataType.SparkleEvent);
  return { importedData: result };
};
